/** @jsxImportSource @emotion/react */
import { useState, useEffect } from "react";
import FormularioCampo from "./FormularioCampo";
import FormularioBoton from "./FormularioBoton";

// La prop formularioEnCuerpo determina si el formulario será renderizado en el cuerpo
// de la página o en un SideBar. Cambia el renderizado de los botones.
// onCambio (opcional) se llama en cada cambio de un campo con el objeto completo
// de valores actuales { nombre: valor }, para cálculos en vivo desde el padre.
// contenidoExtra (opcional) se dibuja después de los campos y antes de los
// botones, para algo que los tipos de campo no cubren (ej. varios archivos).
export default function Formulario({ config, loader, formularioEnCuerpo, onCambio, contenidoExtra }) {
  const [formulario] = useState(config);
  const [campos, setCampos] = useState();
  const [contenidoCampos, setContenidoCampos] = useState({});
  const [estadoCampos, setEstadoCampos] = useState({});
  const [mensajeCampos, setMensajeCampos] = useState({});
  const [archivo, setArchivo] = useState();

  // Al inicializar el formulario, crea un objeto por cada campo
  // con el nombre para ingresar el valor dinámicamente
  // Esto facilita luego el callback de submit, ya se entrega
  // un objeto listo para usar
  useEffect(() => {
    let contenidoCamposInicial = {};
    let camposInicial = [];
    if (formulario.secciones) {
      formulario.secciones.forEach((seccion) => {
        seccion.campos.forEach((campo) => {
          // Si es un dropdown con opción inicial, la carga
          if (campo.tipoitemformularioid === 5 && !campo.sinOpcionInicial) {
            contenidoCamposInicial[campo.nombre] =
              campo.opciones?.length > 0 ? campo.opciones[0].value : null;
            return;
          }
          // Si es un dropdown sin opción inicial, carga la respuesta si existe
          if (campo.tipoitemformularioid === 5 && campo.sinOpcionInicial) {
            contenidoCamposInicial[campo.nombre] = campo.respuesta || null;
            camposInicial.push(campo);
            return;
          }
          // Si es multi-select dropdown o multi-text list, inicializa como array
          if (campo.tipoitemformularioid === 12 || campo.tipoitemformularioid === 13) {
            contenidoCamposInicial[campo.nombre] = Array.isArray(campo.respuesta)
              ? campo.respuesta
              : [];
            camposInicial.push(campo);
            return;
          }
          // Si es un campo de precio, inicializa el objeto { precio, monedaId }
          // para que la moneda por defecto se envíe aunque no se interactúe.
          if (campo.tipoitemformularioid === 14) {
            const r = campo.respuesta || {};
            contenidoCamposInicial[campo.nombre] = {
              precio: r.precio ?? null,
              monedaId: r.monedaId ?? null
            };
            camposInicial.push(campo);
            return;
          }
          // Si es checkbox, inicializa con el estado actual para que se envíe
          // aunque el usuario no interactúe con el campo
          if (campo.tipoitemformularioid === 6) {
            contenidoCamposInicial[campo.nombre] = campo.respuesta ?? false;
            camposInicial.push(campo);
            return;
          }
          contenidoCamposInicial[campo.nombre] = campo.respuesta
            ? campo.respuesta
            : null;
          camposInicial.push(campo);
        });
      });
    } else {
      formulario.campos.forEach((campo) => {
        // Si es un dropdown con opción inicial, la carga
        if (campo.tipoitemformularioid === 5 && !campo.sinOpcionInicial) {
          contenidoCamposInicial[campo.nombre] =
            campo.opciones?.length > 0 ? campo.opciones[0].value : null;
          return;
        }
        // Si es un dropdown sin opción inicial, carga la respuesta si existe
        if (campo.tipoitemformularioid === 5 && campo.sinOpcionInicial) {
          contenidoCamposInicial[campo.nombre] = campo.respuesta || null;
          camposInicial.push(campo);
          return;
        }
        // Si es multi-select dropdown, inicializa como array
        if (campo.tipoitemformularioid === 12) {
          contenidoCamposInicial[campo.nombre] = Array.isArray(campo.respuesta)
            ? campo.respuesta
            : [];
          camposInicial.push(campo);
          return;
        }
        // Si es un campo de precio, inicializa el objeto { precio, monedaId }.
        if (campo.tipoitemformularioid === 14) {
          const r = campo.respuesta || {};
          contenidoCamposInicial[campo.nombre] = {
            precio: r.precio ?? null,
            monedaId: r.monedaId ?? null
          };
          camposInicial.push(campo);
          return;
        }
        // Si es checkbox, inicializa con el estado actual para que se envíe
        // aunque el usuario no interactúe con el campo
        if (campo.tipoitemformularioid === 6) {
          contenidoCamposInicial[campo.nombre] = campo.respuesta ?? false;
          camposInicial.push(campo);
          return;
        }
        contenidoCamposInicial[campo.nombre] = campo.respuesta
          ? campo.respuesta
          : null;
        camposInicial.push(campo);
      });
    }
    setContenidoCampos(contenidoCamposInicial);
    setCampos(camposInicial);
  }, [formulario]);

  // Función llamada en cada update de un campo, lo actualiza
  // en el padre para poder tener los datos para el submit
  function actualizarContenidoCampo(nombre, contenido) {
    let contenidoActual = { ...contenidoCampos };
    contenidoActual[nombre] = contenido;
    setContenidoCampos(contenidoActual);
    if (onCambio) onCambio(contenidoActual);
    // Si un campo es requerido, está en error y ahora tiene contenido
    // se actualiza el estado
    if (
      estadoCampos[nombre] === "error" &&
      contenidoActual[nombre] !== null &&
      contenidoActual[nombre] !== undefined &&
      contenidoActual[nombre] !== "" &&
      !(Array.isArray(contenidoActual[nombre]) && contenidoActual[nombre].length === 0)
    ) {
      let estadoCamposActual = { ...estadoCampos };
      estadoCamposActual[nombre] = "";
      setEstadoCampos(estadoCamposActual);
    }
  }

  // Función llamada en el update del campo file
  function actualizarArchivo(nombre, archivo) {
    if (!archivo) {
      return;
    }
    // Guarda el archivo para enviar
    setArchivo(archivo);
    // Actualiza el contenido del campo para poder trackearlo
    let contenidoActual = { ...contenidoCampos };
    contenidoActual[nombre] = archivo.name;
    setContenidoCampos(contenidoActual);
    if (onCambio) onCambio(contenidoActual);
    // Si un campo es requerido, está en error y ahora tiene contenido
    // se actualiza el estado
    if (
      estadoCampos[nombre] === "error" &&
      contenidoActual[nombre] !== null &&
      contenidoActual[nombre] !== undefined &&
      contenidoActual[nombre] !== ""
    ) {
      let estadoCamposActual = { ...estadoCampos };
      estadoCamposActual[nombre] = "";
      setEstadoCampos(estadoCamposActual);
    }
  }

  // Función ejecutada al presionar un botón que no es el submit
  function botonCallback(callbackDelBoton, validar) {
    // Si está enviando, ignora el submit
    if (loader) {
      return;
    }

    // Si el botón no pide validación, ejecuta el callback
    // Le pasa el contenido de los campos por si lo necesita
    if (!validar) {
      callbackDelBoton(contenidoCampos);
      return;
    }
    let camposOk = true;
    let estadoCamposActual = {};
    let mensajeCamposActual = {};
    // El dominio de primer nivel se valida con largo mínimo 2 y sin máximo.
    // Antes era {2,3}, lo que rechazaba TLDs perfectamente válidos y bastante
    // comunes: .info, .name, .email, .online, .legal, .company.
    const emailRegEx = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,})+$/;
    campos.forEach((campo) => {
      // Si algún campo requerido no tiene contenido
      if (
        campo.requerido &&
        (contenidoCampos[campo.nombre] === null ||
          contenidoCampos[campo.nombre] === undefined ||
          contenidoCampos[campo.nombre] === "")
      ) {
        // Marca el flag como false para no submittear
        camposOk = false;
        estadoCamposActual[campo.nombre] = "error";
        mensajeCamposActual[campo.nombre] = "Campo obligatorio";
      }

      // Si el campo tiene un formato, verifica que lo cumpla
      if (
        campo.regex &&
        contenidoCampos[campo.nombre] &&
        !contenidoCampos[campo.nombre].match(new RegExp(campo.regex))
      ) {
        camposOk = false;
        estadoCamposActual[campo.nombre] = "error";
        mensajeCamposActual[campo.nombre] = "Formato incorrecto";
      }

      // Verifica que el campo email tenga formato de email
      if (
        campo.tipoitemformularioid === 2 &&
        contenidoCampos[campo.nombre] &&
        !contenidoCampos[campo.nombre].match(emailRegEx)
      ) {
        camposOk = false;
        estadoCamposActual[campo.nombre] = "error";
        mensajeCamposActual[campo.nombre] = "Formato incorrecto";
      }

      // Si el campo es de entero, verifica que sea un entero
      if (
        campo.tipoitemformularioid === 4 &&
        contenidoCampos[campo.nombre] &&
        (isNaN(contenidoCampos[campo.nombre]) ||
          contenidoCampos[campo.nombre] % 1 !== 0)
      ) {
        camposOk = false;
        estadoCamposActual[campo.nombre] = "error";
        mensajeCamposActual[campo.nombre] = "El número debe ser entero";
      }

      // Si el campo es de float, verifica que sea un número
      if (
        campo.tipoitemformularioid === 8 &&
        contenidoCampos[campo.nombre] &&
        isNaN(contenidoCampos[campo.nombre])
      ) {
        camposOk = false;
        estadoCamposActual[campo.nombre] = "error";
        mensajeCamposActual[campo.nombre] = "El campo debe ser un número";
      }

      // Si el campo de precio es requerido, verifica que tenga un precio cargado
      // (0 es válido; null/vacío no). La moneda no bloquea el envío.
      if (
        campo.requerido &&
        campo.tipoitemformularioid === 14 &&
        (contenidoCampos[campo.nombre] == null ||
          contenidoCampos[campo.nombre].precio == null)
      ) {
        camposOk = false;
        estadoCamposActual[campo.nombre] = "error";
        mensajeCamposActual[campo.nombre] = "Campo obligatorio";
      }

      // Si el campo es multi-select y es requerido, verifica que tenga al menos una selección
      if (
        campo.requerido &&
        (campo.tipoitemformularioid === 12 || campo.tipoitemformularioid === 13) &&
        Array.isArray(contenidoCampos[campo.nombre]) &&
        contenidoCampos[campo.nombre].length === 0
      ) {
        camposOk = false;
        estadoCamposActual[campo.nombre] = "error";
        mensajeCamposActual[campo.nombre] = "Campo obligatorio";
      }

    });
    if (!camposOk) {
      setEstadoCampos(estadoCamposActual);
      setMensajeCampos(mensajeCamposActual);
      return;
    }
    // Una vez que verificó todos los campos, llama al callback del botón
    // Si tiene un archivo, lo pasa como segundo parámetro
    if (archivo) {
      callbackDelBoton(contenidoCampos, archivo);
    } else {
      callbackDelBoton(contenidoCampos);
    }
  }

  return (
    <div
      css={{
        marginBottom: formularioEnCuerpo ? "0px" : "80px",
      }}
    >
      <div>
        {formulario.secciones &&
          formulario.secciones.map((seccion, seccionIndex) => {
            return (
              <div key={seccionIndex}>
                {seccion.nombre && (
                  <div css={{ fontSize: "1.5em", marginBottom: "1em" }}>
                    {seccion.nombre}
                  </div>
                )}
                {seccion.descripcion && <p>{seccion.descripcion}</p>}
                {seccion.campos.map((campo, index) => {
                  return (
                    <FormularioCampo
                      onUpdate={actualizarContenidoCampo}
                      actualizarArchivo={actualizarArchivo}
                      campo={campo}
                      estado={estadoCampos[campo.nombre]}
                      mensaje={mensajeCampos[campo.nombre]}
                      key={"campo" + index}
                      disabled={loader || campo.disabled}
                      contenidoCampos={contenidoCampos}
                    />
                  );
                })}
              </div>
            );
          })}
      </div>
      {contenidoExtra}
      <div
        css={{
          display: "flex",
          justifyContent: "space-around",
          padding: formularioEnCuerpo
            ? "10px 0px 10px 0px"
            : "10px 10px 10px 0px",
          gap: "10px",
          width: [
            "-webkit-fill-available",
            "-moz-available",
            "100%",
            "stretch",
          ],
          position: formularioEnCuerpo ? "inherit" : "absolute",
          bottom: formularioEnCuerpo ? "inherit" : "0px",
          backgroundColor: formularioEnCuerpo ? "inherit" : "white",
          zIndex: formularioEnCuerpo ? "inherit" : 1,
        }}
      >
        {formulario.botones &&
          formulario.botones.map((boton, index) => {
            return (
              <FormularioBoton
                config={boton}
                loader={loader}
                botonCallback={botonCallback}
                key={"boton" + index}
              />
            );
          })}
      </div>
    </div>
  );
}
